import React, { useState, useEffect } from "react";
import { useProfile, useUpdateProfile } from "../hooks/useProfile.ts";
import { State, City } from 'country-state-city';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import { useTheme } from "../contexts/ThemeContext.tsx";
import { getTheme } from '../theme.ts';
import toast from "react-hot-toast";
import { FaYoutube, FaInstagram, FaFacebook, FaTwitter, FaLinkedin, FaGlobe, FaLink } from 'react-icons/fa';

interface ProfileEditProps {
  onCancel: () => void;
}

const ProfileEdit: React.FC<ProfileEditProps> = ({ onCancel }) => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const reawdata = localStorage.getItem('user');
  let cred = reawdata ? JSON.parse(reawdata) : null;
  let token = cred?.access;

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    date_of_birth: "",
    district: "",
    state: "",
    pincode: "",
    interests: "",
    profile_picture: null as File | null,
    // Individual social media links
    youtube: "",
    instagram: "",
    facebook: "",
    twitter: "",
    linkedin: "",
    website: "",
  });

  const { data: profileData, isLoading: isLoadingProfile, isError: isProfileError } = useProfile(cred?.user?.id);
  const updateProfileMutation = useUpdateProfile();

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Get cities for India
  const cities = City.getCitiesOfCountry('IN');

  // Validation functions
  const validateRequired = (value: string) => value.trim() !== "";
  const validateDate = (value: string) => {
    if (!value) return true;
    const date = new Date(value);
    const today = new Date();
    return date <= today;
  };
  const validatePincode = (value: string) => /^\d{6}$/.test(value);
  const validateUrl = (value: string) => {
    if (!value) return true;
    try {
      new URL(value.startsWith('http') ? value : `https://${value}`);
      return true;
    } catch {
      return false;
    }
  };

  const validateField = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "first_name":
      case "last_name":
        if (!validateRequired(value)) error = `${name.replace("_", " ")} is required`;
        break;
      case "phone_number":
        if (value && !isValidPhoneNumber(value)) error = "Invalid phone number";
        break;
      case "date_of_birth":
        if (!validateDate(value)) error = "Date of birth cannot be in the future";
        break;
      case "pincode":
        if (value && !validatePincode(value)) error = "Pincode must be 6 digits";
        break;
      case "youtube":
      case "instagram":
      case "facebook":
      case "twitter":
      case "linkedin":
      case "website":
        if (value && !validateUrl(value)) error = "Please enter a valid URL";
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  useEffect(() => {
    // Only load if not loading and we have data or error
    if (isLoadingProfile) return;

    // Use profileData from API if available, otherwise fall back to localStorage
    const sourceData = profileData?.user?.profile || profileData || cred?.user?.profile;

    if (sourceData) {
      console.log('Loading profile data from:', profileData ? 'API' : 'LocalStorage');

      let initialProfileLinks = {};
      if (sourceData?.profile_links) {
        if (typeof sourceData.profile_links === 'string') {
          try {
            initialProfileLinks = JSON.parse(sourceData.profile_links);
          } catch {
            initialProfileLinks = {};
          }
        } else {
          initialProfileLinks = sourceData.profile_links;
        }
      }

      setFormData({
        first_name: sourceData?.first_name || "",
        last_name: sourceData?.last_name || "",
        phone_number: sourceData?.phone_number || "",
        date_of_birth: sourceData?.date_of_birth || "",
        district: sourceData?.district || "",
        state: sourceData?.state || "",
        pincode: sourceData?.pincode || "",
        interests: (() => {
          if (Array.isArray(sourceData?.interests)) {
            if (sourceData.interests.length > 0 && Array.isArray(sourceData.interests[0])) {
              const flattened = sourceData.interests.flat();
              return flattened.length > 0 ? flattened.join(", ") : "";
            }
            // Handle mixed content or pure strings
            const cleanedInterests = sourceData.interests.flatMap((item: string | string[] | null) => {
              if (typeof item === 'string') {
                const cleaned = item.replace(/^\["?|"?\]?$|"/g, '').trim();
                return cleaned ? [cleaned] : [];
              } else if (Array.isArray(item)) {
                return item.flat();
              }
              return [];
            });
            const uniqueInterests = Array.from(new Set(cleanedInterests));
            return uniqueInterests.join(", ");
          } else {
            return sourceData?.interests || "";
          }
        })(),
        profile_picture: null as File | null,
        youtube: initialProfileLinks?.youtube?.link || initialProfileLinks?.youtube || "",
        instagram: initialProfileLinks?.instagram?.link || initialProfileLinks?.instagram || "",
        facebook: initialProfileLinks?.facebook?.link || initialProfileLinks?.facebook || "",
        twitter: initialProfileLinks?.twitter?.link || initialProfileLinks?.twitter || "",
        linkedin: initialProfileLinks?.linkedin?.link || initialProfileLinks?.linkedin || "",
        website: initialProfileLinks?.website?.link || initialProfileLinks?.website || "",
      });

      if (sourceData?.profile_picture) {
        setPreviewImage(`https://api.socialscollab.com${sourceData.profile_picture}`);
      }
    }
  }, [profileData, isLoadingProfile, cred]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setFormData({ ...formData, profile_picture: file });

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      toast.success("Profile picture selected!");
    }
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = e.target.selectedOptions[0];
    const state = selectedOption.getAttribute('data-state') || '';
    setFormData(prev => ({ ...prev, district: e.target.value, state }));

    // Clear errors
    if (errors.district) {
      setErrors(prev => ({ ...prev, district: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submission
    const fieldsToValidate = ['first_name', 'last_name', 'phone_number', 'date_of_birth', 'pincode', 'youtube', 'instagram', 'facebook', 'twitter', 'linkedin', 'website'];
    let hasErrors = false;
    const newErrors: { [key: string]: string } = {};

    fieldsToValidate.forEach(field => {
      const value = formData[field as keyof typeof formData] as string;
      let error = "";
      switch (field) {
        case "first_name":
        case "last_name":
          if (!validateRequired(value)) error = `${field.replace("_", " ")} is required`;
          break;
        case "phone_number":
          if (value && !isValidPhoneNumber(value)) error = "Invalid phone number";
          break;
        case "date_of_birth":
          if (!validateDate(value)) error = "Date of birth cannot be in the future";
          break;
        case "pincode":
          if (value && !validatePincode(value)) error = "Pincode must be 6 digits";
          break;
        case "youtube":
        case "instagram":
        case "facebook":
        case "twitter":
        case "linkedin":
        case "website":
          if (value && !validateUrl(value)) error = "Please enter a valid URL";
          break;
      }
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      // Show error toast for validation failures
      toast.error("Please fix the validation errors before submitting");
      return;
    }

    setLoading(true);

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "profile_picture" && value !== null) {
        data.append(key, value as File);
      } else if (key === "interests" && value && typeof value === 'string') {
        // Convert comma-separated string back to array and remove duplicates
        const interestsArray = Array.from(new Set(value.split(',').map((interest: string) => interest.trim()).filter((interest: string) => interest)));
        data.append(key, JSON.stringify(interestsArray));
      } else if (key !== "profile_picture" && value !== null && value !== undefined) {
        data.append(key, String(value));
      }
    });

    // Build profile_links object from individual social media fields
    const profileLinks: any = {};
    const socialPlatforms = ['youtube', 'instagram', 'facebook', 'twitter', 'linkedin', 'website'];

    socialPlatforms.forEach(platform => {
      const link = formData[platform as keyof typeof formData] as string;
      if (link && link.trim()) {
        profileLinks[platform] = {
          link: link.trim(),
          status: 'PENDING' // Default status
        };
      }
    });

    // Add profile_links to form data
    if (Object.keys(profileLinks).length > 0) {
      data.append('profile_links', JSON.stringify(profileLinks));
    }

    console.log('Submitting form data:');
    for (let [key, value] of data.entries()) {
      console.log(key, value);
    }

    try {
      const loadingToast = toast.loading("Updating your profile...");

      const updatedData = await updateProfileMutation.mutateAsync(data);
      console.log('Update response:', updatedData)

      if (updatedData) {
        // Update localStorage with the new form data
        const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUserData.user?.profile) {
          // Convert form data back to the expected format for localStorage
          const updatedProfile = { ...currentUserData.user.profile };

          // Handle interests conversion back to array format
          if (formData.interests && typeof formData.interests === 'string') {
            const interestsArray = formData.interests.split(',').map((interest: string) => interest.trim()).filter((interest: string) => interest);
            updatedProfile.interests = interestsArray;
          }

          // Update profile links
          updatedProfile.profile_links = profileLinks;

          // Update other fields
          Object.keys(formData).forEach(key => {
            if (key !== 'interests' && key !== 'profile_links' && key !== 'profile_picture' && !socialPlatforms.includes(key)) {
              updatedProfile[key] = formData[key as keyof typeof formData];
            }
          });

          currentUserData.user.profile = updatedProfile;
          localStorage.setItem('user', JSON.stringify(currentUserData));
        }

        toast.dismiss(loadingToast);

        // Delay the navigation slightly so user can see the success message
        setTimeout(() => {
          onCancel();
        }, 1000);
      } else {
        toast.dismiss(loadingToast);
      }
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    toast("Profile edit cancelled", {
      icon: "⚠️",
      duration: 3000,
    });
    onCancel();
  };

  // Social media platform configuration
  const socialPlatforms = [
    { key: 'youtube', label: 'YouTube', icon: FaYoutube, color: 'text-red-600', placeholder: 'https://youtube.com/yourchannel' },
    { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: 'text-pink-600', placeholder: 'https://instagram.com/username' },
    { key: 'facebook', label: 'Facebook', icon: FaFacebook, color: 'text-blue-600', placeholder: 'https://facebook.com/username' },
    { key: 'twitter', label: 'Twitter', icon: FaTwitter, color: 'text-blue-400', placeholder: 'https://twitter.com/username' },
    { key: 'linkedin', label: 'LinkedIn', icon: FaLinkedin, color: 'text-blue-700', placeholder: 'https://linkedin.com/in/username' },
    { key: 'website', label: 'Website', icon: FaGlobe, color: 'text-green-600', placeholder: 'https://yourwebsite.com' },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold" style={{ color: currentTheme.text }}>Edit Your Profile</h2>
        <p style={{ color: currentTheme.textSecondary }}>Update your personal information</p>
      </div>

      {/* Profile Picture Upload */}
      <div className="flex flex-col items-center mb-6">
        <div className="relative mb-4">
          {previewImage ? (
            <img
              src={previewImage}
              alt="Profile preview"
              className="w-32 h-32 rounded-full shadow-lg object-cover"
              style={{ border: `4px solid ${currentTheme.border}` }}
            />
          ) : (
            <div className="w-32 h-32 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: currentTheme.surface, border: `4px solid ${currentTheme.border}` }}>
              <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20" style={{ color: currentTheme.textSecondary }}>
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>

        <label style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }} className="px-6 py-2 rounded-lg cursor-pointer hover:opacity-90 transition-colors font-medium shadow-lg">
          <span>Change Photo</span>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
        {formData.profile_picture && (
          <p className="text-sm mt-2" style={{ color: currentTheme.textSecondary }}>
            New image selected: {formData.profile_picture.name}
          </p>
        )}
      </div>

      {/* Form Fields Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="First Name"
          name="first_name"
          type="text"
          value={formData.first_name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter your first name"
          required
          error={errors.first_name}
          theme={currentTheme}
        />

        <FormField
          label="Last Name"
          name="last_name"
          type="text"
          value={formData.last_name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter your last name"
          required
          error={errors.last_name}
          theme={currentTheme}
        />

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>Phone Number</label>
          <PhoneInput
            value={formData.phone_number}
            onChange={(value) => setFormData({ ...formData, phone_number: value || '' })}
            defaultCountry="IN"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white ${errors.phone_number ? 'border-red-500' : 'border-primary-300'}`}
          />
          {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
        </div>

        <FormField
          label="Date of Birth"
          name="date_of_birth"
          type="date"
          value={formData.date_of_birth}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.date_of_birth}
          theme={currentTheme}
        />

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>District</label>
          <select
            name="district"
            value={formData.district}
            onChange={handleDistrictChange}
            className="w-full px-4 py-3 border border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white"
          >
            <option value="">Select District</option>
            {cities?.map(city => {
              const stateName = State.getStateByCodeAndCountry(city.stateCode, 'IN')?.name || '';
              return <option key={city.name} value={city.name} data-state={stateName}>{city.name}</option>;
            })}
          </select>
        </div>

        <FormField
          label="State"
          name="state"
          type="text"
          value={formData.state}
          onChange={handleChange}
          placeholder="Enter your state"
          disabled
          theme={currentTheme}
        />

        <FormField
          label="Pincode"
          name="pincode"
          type="text"
          value={formData.pincode}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter your pincode"
          error={errors.pincode}
          theme={currentTheme}
        />
      </div>

      {/* Full Width Fields */}
      <div className="space-y-6">
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
            Interests
          </label>
          <CreatableSelect
            isMulti
            onChange={(selectedOptions: MultiValue<{ value: string; label: string }>) => {
              // Remove duplicates by using Set
              const uniqueInterests = Array.from(new Set(selectedOptions ? selectedOptions.map(option => option.value.trim()).filter(Boolean) : []));
              setFormData(prev => ({ ...prev, interests: uniqueInterests.join(", ") }));
            }}
            options={[
              { value: 'Photography', label: 'Photography' },
              { value: 'Travel', label: 'Travel' },
              { value: 'Technology', label: 'Technology' },
              { value: 'Music', label: 'Music' },
              { value: 'Sports', label: 'Sports' },
              { value: 'Art', label: 'Art' },
              { value: 'Fashion', label: 'Fashion' },
              { value: 'Food', label: 'Food' },
              { value: 'Gaming', label: 'Gaming' },
              { value: 'Fitness', label: 'Fitness' },
              { value: 'Business', label: 'Business' },
              { value: 'Education', label: 'Education' },
              { value: 'Health', label: 'Health' },
              { value: 'Lifestyle', label: 'Lifestyle' },
              { value: 'Entertainment', label: 'Entertainment' },
              { value: 'Science', label: 'Science' },
              { value: 'Nature', label: 'Nature' },
              { value: 'Cooking', label: 'Cooking' },
              { value: 'Writing', label: 'Writing' },
              { value: 'Dancing', label: 'Dancing' },
            ]}
            value={formData.interests ? Array.from(new Set(formData.interests.split(", ").map((i: string) => i.trim()).filter(Boolean))).map((i: string) => ({ value: i, label: i })) : []}
            placeholder="Select or type your interests"
            className="basic-multi-select"
            classNamePrefix="select"
          />
          <p className="text-xs text-primary-500 mt-1">
            Select from options or type your own interests
          </p>
        </div>

        {/* Social Media Links */}
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-4" style={{ color: currentTheme.text }}>
            <FaLink className="inline mr-2" />
            Social Media Links
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {socialPlatforms.map((platform) => {
              const Icon = platform.icon;
              return (
                <div key={platform.key} className="flex items-center space-x-3">
                  <Icon className={`text-xl ${platform.color} flex-shrink-0`} />
                  <div className="flex-1">
                    <input
                      type="text"
                      name={platform.key}
                      value={formData[platform.key as keyof typeof formData] as string}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={platform.placeholder}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white ${errors[platform.key] ? 'border-red-500' : 'border-primary-300'
                        }`}
                    />
                    {errors[platform.key] && (
                      <p className="text-red-500 text-xs mt-1">{errors[platform.key]}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-primary-500 mt-3">
            Enter your social media profile URLs. You can enter full URLs (https://...) or just the username.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-primary-100">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-3 px-6 rounded-lg transition-all duration-300 font-semibold flex items-center justify-center shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: currentTheme.primary,
            color: currentTheme.inverseText,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = currentTheme.primary + 'CC'; // slightly transparent
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = currentTheme.primary;
          }}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Updating...
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Changes
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleCancel}
          className="flex-1 py-3 px-6 rounded-lg transition-all duration-300 font-semibold flex items-center justify-center shadow-lg"
          style={{
            backgroundColor: currentTheme.surface,
            color: currentTheme.text,
            border: `1px solid ${currentTheme.border}`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = currentTheme.surface + 'CC';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = currentTheme.surface;
          }}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Cancel
        </button>
      </div>
    </form>
  );
};

// Reusable FormField Component
const FormField: React.FC<{
  label: string;
  name: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  error?: string;
  disabled?: boolean;
  theme: any;
}> = ({ label, name, type, value, onChange, onBlur, placeholder, required = false, fullWidth = false, error, disabled = false, theme }) => (
  <div className={`${fullWidth ? 'col-span-2' : ''}`}>
    <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white placeholder-primary-400 ${error ? 'border-red-500' : 'border-primary-300'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

export default ProfileEdit;